/**
 * Custom hook for managing T-code list state and logic
 */

import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { PayloadAction } from '@reduxjs/toolkit';

import { auditLogActions } from '../../pages/AuditLogViewer/data-store/auditLogSlice';
import { INITIAL_ITEMS_COUNT } from '../constants';
import { type TCodeData } from '../types';
import { paginateTCodes, sortTCodes } from '../utils';

import { useTypedSelector } from './use-typedselector';

// Type-safe wrapper for the action
// The action creator is properly typed in auditLogSlice.ts as (payload: boolean) => PayloadAction<boolean>
// We use a type assertion here because TypeScript can't infer the types through the cross-directory import
interface AuditLogActionsType {
  setTCodeListExpanded: (payload: boolean) => PayloadAction<boolean>;
}

const typedAuditLogActions = auditLogActions as AuditLogActionsType;

const setTCodeListExpanded = (payload: boolean): PayloadAction<boolean> =>
  typedAuditLogActions.setTCodeListExpanded(payload);

interface UseTCodeListProps {
  tCodes: TCodeData[];
  onCodeClick?: (code: string) => void;
}

interface UseTCodeListReturn {
  // State
  showAll: boolean;
  sortedTCodes: TCodeData[];
  paginatedData: {
    data: TCodeData[];
    totalPages: number;
    currentPage: number;
  };
  hasMoreItems: boolean;
  remainingCount: number;

  // Actions
  handleViewMore: () => void;
  handleCodeClick: (codeId: string) => void;
}

/**
 * Custom hook for managing T-code list state and operations
 * @param tCodes - Array of T-code data
 * @param onCodeClick - Optional callback for when a T-code is clicked
 * @returns Object containing state, computed values, and actions
 */
const useTCodeList = ({
  tCodes,
  onCodeClick,
}: UseTCodeListProps): UseTCodeListReturn => {
  const dispatch = useDispatch();
  // Read expanded state from Redux (persists across component remounts)
  const showAll = useTypedSelector(
    (state) => state.aag.auditLog.isTCodeListExpanded
  );

  // State - currentPage is used for pagination
  const [currentPage] = useState(1);

  // Computed values
  // Sort T-codes by severity (CRITICAL → HIGH → MEDIUM → LOW)
  // No filtering needed as searchTerm is not used
  const sortedTCodes = useMemo(() => sortTCodes(tCodes), [tCodes]);

  const paginatedData = useMemo(
    () => paginateTCodes(sortedTCodes, currentPage, INITIAL_ITEMS_COUNT),
    [sortedTCodes, currentPage]
  );

  const hasMoreItems = useMemo(
    () => sortedTCodes.length > INITIAL_ITEMS_COUNT,
    [sortedTCodes]
  );

  const remainingCount = useMemo(
    () => sortedTCodes.length - INITIAL_ITEMS_COUNT,
    [sortedTCodes]
  );

  // Actions
  const handleViewMore = useCallback(() => {
    // Update Redux state to persist across component remounts
    dispatch(setTCodeListExpanded(true));
  }, [dispatch]);

  const handleCodeClick = useCallback(
    (codeId: string) => {
      onCodeClick?.(codeId);
    },
    [onCodeClick]
  );

  return {
    // State
    showAll,
    sortedTCodes,
    paginatedData,
    hasMoreItems,
    remainingCount,

    // Actions
    handleViewMore,
    handleCodeClick,
  };
};

export default useTCodeList;
